import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {DeviceMarkerComponent} from '../device-marker/device-marker.component';
import {DeviceMarkerWithPolledAirDatum} from '../../../floor-plan-shared/models/device-marker';
import {CanvasMapInitializedVariables} from '../../../floor-plan-shared/models/canvas-map';

@Component({
  selector: 'awair-floor-plan-map',
  templateUrl: './floor-plan-map.component.html',
  styleUrls: ['./floor-plan-map.component.scss']
})

export class FloorPlanMapComponent implements OnInit {

  @ViewChild('markerAnchor', {read: ViewContainerRef}) markerAnchor;

  get deviceMarkers(): DeviceMarkerWithPolledAirDatum[] {
    if (this._deviceMarkers != null) {
      return this._deviceMarkers;
    }
  }
  @Input()
  set deviceMarkers(value: DeviceMarkerWithPolledAirDatum[]) {
    if (value != null) {
      this._deviceMarkers = [...value];

      this.updateDeviceMarkers();
    }
  }
  private _deviceMarkers: DeviceMarkerWithPolledAirDatum[] = [];

  get mapUrl(): string {
    if (this._mapUrl != null && this._mapUrl.length > 0) {
      return this._mapUrl;
    }
  }
  @Input()
  set mapUrl(value: string) {
    if (value != null && value.length > 0) {
      this._mapUrl = value;
    } else {
      this.removeAllDeviceMarkers();
      this._mapUrl = null;
    }
  }
  private _mapUrl: string;

  @Output() selectDevice: EventEmitter<string> = new EventEmitter<string>();
  @Output() deselectDevice: EventEmitter<string> = new EventEmitter<string>();

  imageWidth: number = 0;
  imageHeight: number = 0;
  canvasDestinationX: number = 0;
  canvasDestinationY: number = 0;
  zoomRatio: number = 1;

  private deviceMarkerComponentRefMap: { [deviceUuid: string]: ComponentRef<DeviceMarkerComponent> } = {};

  constructor(private sanitizer: DomSanitizer,
              private componentFactoryResolver: ComponentFactoryResolver) {
  }

  ngOnInit() {
  }

  // Initialize device markers after canvas has been initialized
  initDeviceMarkers({imageWidth, imageHeight, dx, dy, canvasWidth, canvasHeight, zoomRatio}: CanvasMapInitializedVariables) {
    this.canvasDestinationX = dx;
    this.canvasDestinationY = dy;
    this.imageWidth = imageWidth;
    this.imageHeight = imageHeight;
    this.zoomRatio = zoomRatio;

    this.updateDeviceMarkers();
  }

  updateDeviceMarkers(): void {
    // Delete devices that doesn't exist in the polling
    const deletedDeviceUuids: string[] = [];
    Object.keys(this.deviceMarkerComponentRefMap).forEach((deviceUuid: string) => {
      const index = this.deviceMarkers.findIndex((deviceMarker: DeviceMarkerWithPolledAirDatum) => deviceMarker.deviceUuid === deviceUuid);
      if (index === -1) {
        this.deviceMarkerComponentRefMap[deviceUuid].destroy();
        deletedDeviceUuids.push(deviceUuid);
      }
    });
    // Delete keys of refMap here
    deletedDeviceUuids.forEach((deletedDeviceUuid: string) => {
      delete this.deviceMarkerComponentRefMap[deletedDeviceUuid];
    });
    // Upsert devices
    this.deviceMarkers.forEach((deviceMarkerWithPolledAirDatum: DeviceMarkerWithPolledAirDatum) => {
      if (this.deviceMarkerComponentRefMap[deviceMarkerWithPolledAirDatum.deviceUuid] == null) {
        this.deviceMarkerComponentRefMap[deviceMarkerWithPolledAirDatum.deviceUuid] =
          this.addDeviceMarker(deviceMarkerWithPolledAirDatum);
      } else if (this.deviceMarkerComponentRefMap[deviceMarkerWithPolledAirDatum.deviceUuid] != null) {
        this.updateDeviceMarker(deviceMarkerWithPolledAirDatum);
      }
    });
  }

  onChangeImagePosition({dx, dy}: {dx: number, dy: number}): void {
    this.canvasDestinationX = dx;
    this.canvasDestinationY = dy;
    this.deviceMarkers.forEach((deviceMarkerWithPolledAirDatum: DeviceMarkerWithPolledAirDatum) => {
      this.updateDeviceMarkerPosition(deviceMarkerWithPolledAirDatum);
    });
  }

  onChangeZoomRatio(zoomRatio: number): void {
    this.zoomRatio = zoomRatio;
    this.deviceMarkers.forEach((deviceMarkerWithPolledAirDatum: DeviceMarkerWithPolledAirDatum) => {
      this.updateDeviceMarkerPosition(deviceMarkerWithPolledAirDatum);
    });
  }

  private addDeviceMarker({x, y, deviceUuid, name, color, isOnline, unit, value, isSelected}: DeviceMarkerWithPolledAirDatum):
    ComponentRef<DeviceMarkerComponent> {

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(DeviceMarkerComponent);

    const componentRef = this.markerAnchor.createComponent(componentFactory);
    (<DeviceMarkerComponent>componentRef.instance).hostLeft =
      this.sanitizer.bypassSecurityTrustStyle(this.getMarkerPoint(this.imageWidth, x, this.zoomRatio, this.canvasDestinationX));
    (<DeviceMarkerComponent>componentRef.instance).hostTop =
      this.sanitizer.bypassSecurityTrustStyle(this.getMarkerPoint(this.imageHeight, y, this.zoomRatio, this.canvasDestinationY));
    (<DeviceMarkerComponent>componentRef.instance).color = isOnline ? color : '#CDD3D8';
    (<DeviceMarkerComponent>componentRef.instance).deviceUuid = deviceUuid;
    (<DeviceMarkerComponent>componentRef.instance).name = name;
    (<DeviceMarkerComponent>componentRef.instance).unit = unit;
    (<DeviceMarkerComponent>componentRef.instance).value = value;
    (<DeviceMarkerComponent>componentRef.instance).isOnline = isOnline;
    (<DeviceMarkerComponent>componentRef.instance).isSelected = isSelected;
    (<DeviceMarkerComponent>componentRef.instance).clickDevice.subscribe(
      (clickedDevice: { deviceUuid: string, isSelected: boolean }) =>
        clickedDevice.isSelected ? this.selectDevice.emit(deviceUuid) : this.deselectDevice.emit(deviceUuid)
    );

    return componentRef;
  }

  private updateDeviceMarker({x, y, deviceUuid, name, color, isOnline, unit, value, isSelected}: DeviceMarkerWithPolledAirDatum): void {
    this.deviceMarkerComponentRefMap[deviceUuid].instance.hostLeft =
      this.sanitizer.bypassSecurityTrustStyle(this.getMarkerPoint(this.imageWidth, x, this.zoomRatio, this.canvasDestinationX));
    this.deviceMarkerComponentRefMap[deviceUuid].instance.hostTop =
      this.sanitizer.bypassSecurityTrustStyle(this.getMarkerPoint(this.imageHeight, y, this.zoomRatio, this.canvasDestinationY));
    this.deviceMarkerComponentRefMap[deviceUuid].instance.name = name;
    this.deviceMarkerComponentRefMap[deviceUuid].instance.value = value;
    this.deviceMarkerComponentRefMap[deviceUuid].instance.unit = unit;
    this.deviceMarkerComponentRefMap[deviceUuid].instance.color = isOnline ? color : '#CDD3D8';
    this.deviceMarkerComponentRefMap[deviceUuid].instance.isOnline = isOnline;
    this.deviceMarkerComponentRefMap[deviceUuid].instance.isSelected = isSelected;
  }

  private updateDeviceMarkerPosition({x, y, deviceUuid, name, color, isOnline, unit, value}: DeviceMarkerWithPolledAirDatum): void {
    this.deviceMarkerComponentRefMap[deviceUuid].instance.hostLeft =
      this.sanitizer.bypassSecurityTrustStyle(this.getMarkerPoint(this.imageWidth, x, this.zoomRatio, this.canvasDestinationX));
    this.deviceMarkerComponentRefMap[deviceUuid].instance.hostTop =
      this.sanitizer.bypassSecurityTrustStyle(this.getMarkerPoint(this.imageHeight, y, this.zoomRatio, this.canvasDestinationY));
  }

  private removeAllDeviceMarkers(): void {
    Object.keys(this.deviceMarkerComponentRefMap).forEach((key: string) => {
      this.deviceMarkerComponentRefMap[key].destroy();
    });
    // Instead of setting all deviceMarkerComponentRef element to null, this is simple way to reset
    this.deviceMarkerComponentRefMap = {};
  }

  private getMarkerPoint(size: number, ratio: number, zoomRatio: number, destinationPoint: number): string {
    return `calc((${size}px * ${ratio}) * ${zoomRatio} - 20px + ${destinationPoint}px)`;
  }
}
